import { HttpParamsBuilder, Meta, Response, Vehicle } from '@agilox/common';
import { HeatmapData } from '@agilox/ui-common';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '@my/env/environment';
import { UserStoreService } from '@user-store';
import * as htmlToImage from 'html-to-image';
import { forkJoin, map, Observable, of } from 'rxjs';
import { MyAppSettings } from '../../general/interfaces/my-app-settings.interface';
import { forecast } from '../pages/maas/utils/maas-calculation.utils';
import { ComparisonData } from './models/comparison-data.interface';
import { CostBreakdown } from './models/cost-breakdown.type';
import { CostData } from './models/cost-data.interface';
import { ForecastedUsage } from './models/forecasted-usage.interface';
import { MaasCostsFilters } from './models/maas-cost-filter-state.interface';
import { MaasCostPeriod } from './models/maas-cost-period.interface';
import {
	MaasAvailableYears,
	MaasAvailableYearsMessage,
	MaasAvailableYearsResponse,
} from './models/maas-available-years.interface';
import { MaasYearlyOverviewRawResponse } from './models/maas-yearly-overview-raw-response.interface';
import { MaasYearlyOverviewResponse } from './models/maas-yearly-overview-response.interface';
import {
	MaasOverviewExportResponse,
	MaasOverviewResponse,
} from './models/maas-overview-response.interface';
import { PngDownload } from './models/png-download.interface';

@Injectable()
export class MaasService {
	private httpClient = inject(HttpClient);
	private userStoreService = inject<UserStoreService<MyAppSettings>>(UserStoreService);

	public filters = computed(() => this._filters());

	public currentPeriod = computed(() => {
		return this.filters()?.period;
	});

	public firstPeriod = computed(() => {
		const periods = this.availablePeriods();
		if (!periods || periods.length === 0) {
			return undefined;
		}
		return periods.sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime())[0];
	});

	public hasPreviousPeriod = computed<boolean>(() => {
		const current = this.currentPeriod();
		if (!current) {
			return false;
		}
		if (!this.availablePeriods()?.length) {
			return false;
		}
		return this.getPreviousPeriod(current) !== undefined;
	});

	public previousPeriod = computed(() => {
		const current = this.currentPeriod();
		if (!current) return undefined;
		return this.getPreviousPeriod(current);
	});

	public adjustedPreviousPeriodWindow = computed(() => {
		const current = this.currentPeriod();
		const previous = this.previousPeriod();
		if (!current || !previous) return null;
		return this.getAdjustedPreviousPeriodWindow(current, previous);
	});

	public adjustedPreviousPeriodFrom = computed(
		() => this.adjustedPreviousPeriodWindow()?.from ?? this.previousPeriod()?.from
	);

	public adjustedPreviousPeriodTo = computed(
		() => this.adjustedPreviousPeriodWindow()?.to ?? this.previousPeriod()?.to
	);

	private _filters = signal<MaasCostsFilters | undefined>(undefined);

	public availablePeriods = signal<MaasCostPeriod[]>([]);

	public getPreviousPeriod(period: MaasCostPeriod): MaasCostPeriod | undefined {
		if (!this.availablePeriods()?.length) {
			return undefined;
		}
		const previousPeriods = this.availablePeriods().filter(
			(p: MaasCostPeriod) => new Date(p.from).getTime() < new Date(period.from).getTime()
		);
		if (previousPeriods.length === 0) {
			return undefined;
		}
		return previousPeriods[previousPeriods.length - 1];
	}

	fetchComparisonData(filters: MaasCostsFilters): Observable<ComparisonData> {
		const currentPeriodReq = this.fetchUsageDataForTimePeriod(
			filters.vehicle,
			filters.period.from,
			filters.period.to
		);
		const previousReq = this.fetchPreviousPeriodUsageData(filters.vehicle, filters.period);
		const averageReq = this.fetchAverageUsageData(filters.vehicle, filters.period);

		return forkJoin([currentPeriodReq, previousReq, averageReq]).pipe(
			map(([current, previous, average]) => {
				if (current === undefined || previous === undefined || average === undefined) {
					return null;
				}
				return {
					previousPeriod: {
						hours: current - previous,
						percentage: previous ? ((current - previous) / previous) * 100 : 0,
					},
					average: {
						hours: current - average,
						percentage: average ? ((current - average) / average) * 100 : 0,
					},
				};
			})
		);
	}

	// Adding some context here - we compare current period to previous one.
	// If previous one spans multiple months, we take the last month.
	// Extra special case - if end of previous period and start of current period are the same month
	// we use the month before that for comparison (eg. if start of current and end of last is Oct, we use Sep)
	private getAdjustedPreviousPeriodWindow(
		period: MaasCostPeriod,
		previousPeriod: MaasCostPeriod
	): { from: string; to: string } {
		const prevFrom = new Date(previousPeriod.from);
		const prevTo = new Date(previousPeriod.to);
		const monthDiff =
			(prevTo.getUTCFullYear() - prevFrom.getUTCFullYear()) * 12 +
			(prevTo.getUTCMonth() - prevFrom.getUTCMonth());

		let windowFrom: Date;
		let windowTo: Date;

		if (monthDiff >= 1) {
			let comparisonYear = prevTo.getUTCFullYear();
			let comparisonMonth = prevTo.getUTCMonth();
			const currentFrom = new Date(period.from);
			if (
				comparisonMonth === currentFrom.getUTCMonth() &&
				comparisonYear === currentFrom.getUTCFullYear()
			) {
				comparisonMonth -= 1;
				if (comparisonMonth < 0) {
					comparisonMonth = 11;
					comparisonYear -= 1;
				}
			}
			const lastMonthStart = new Date(Date.UTC(comparisonYear, comparisonMonth, 1));
			const lastMonthEnd = new Date(Date.UTC(comparisonYear, comparisonMonth + 1, 0));
			windowFrom = lastMonthStart < prevFrom ? prevFrom : lastMonthStart;
			windowTo = lastMonthEnd > prevTo ? prevTo : lastMonthEnd;
		} else {
			windowFrom = prevFrom;
			windowTo = prevTo;
		}

		if (period.current_period) {
			const daysElapsed = Math.floor(
				(new Date(period.to).getTime() - new Date(period.from).getTime()) / (1000 * 60 * 60 * 24)
			);
			windowTo = new Date(windowFrom.getTime() + daysElapsed * 24 * 60 * 60 * 1000);
		}

		return {
			from: windowFrom.toISOString().split('T')[0],
			to: windowTo.toISOString().split('T')[0],
		};
	}

	private fetchPreviousPeriodUsageData(
		vehicle: Vehicle,
		period: MaasCostPeriod
	): Observable<number> {
		const previousPeriod = this.getPreviousPeriod(period);
		if (!previousPeriod) {
			return of(0);
		}

		const { from, to } = this.getAdjustedPreviousPeriodWindow(period, previousPeriod);
		return this.fetchUsageDataForTimePeriod(vehicle, from, to).pipe(map((data) => data || 0));
	}

	private fetchAverageUsageData(vehicle: Vehicle, period: MaasCostPeriod): Observable<number> {
		// get previous 11 periods, including the current one
		// if there are not enough periods, take as many as possible
		const availablePeriods = this.availablePeriods();
		const currentPeriodIndex = availablePeriods.findIndex((p) => p.from === period.from);
		if (currentPeriodIndex === -1) {
			return of(0);
		}
		const previousPeriods = availablePeriods.slice(
			Math.max(0, currentPeriodIndex - 11),
			currentPeriodIndex // exclude current period
		);
		if (previousPeriods.length === 0) {
			return of(0);
		}

		if (!period.current_period) {
			const start = previousPeriods[0].from;
			const end = previousPeriods[previousPeriods.length - 1].to;
			return this.fetchUsageDataForTimePeriod(vehicle, start, end).pipe(
				map((data) => (data ? data / previousPeriods.length : 0))
			);
		}

		const daysElapsed = Math.floor(
			(new Date(period.to).getTime() - new Date(period.from).getTime()) / (1000 * 60 * 60 * 24)
		);
		return forkJoin(
			previousPeriods.map((p) => {
				const adjustedTo = new Date(p.from);
				adjustedTo.setDate(adjustedTo.getDate() + daysElapsed);
				return this.fetchUsageDataForTimePeriod(
					vehicle,
					p.from,
					adjustedTo.toISOString().split('T')[0]
				).pipe(map((data) => data || 0));
			})
		).pipe(map((results) => results.reduce((sum, val) => sum + val, 0) / previousPeriods.length));
	}

	private fetchUsageDataForTimePeriod(vehicle: Vehicle, from: string, to: string): Observable<any> {
		let paramStr = `filter[serial]=${vehicle.serial}&filter[from.gte]=${from}&filter[to.lte]=${to}`;
		return this.httpClient
			.get<{
				data: { date: string; val: number }[];
				status: boolean;
			}>(environment.server + `/v2/maas/workload?${paramStr}`)
			.pipe(
				map((response) => {
					if (!response.status) {
						return 0;
					}
					const data = response.data;
					return data.reduce((acc, item) => {
						return acc + item.val;
					}, 0);
				})
			);
	}

	fetchYearlyBusyTimeData(filters: MaasCostsFilters): Observable<HeatmapData[]> {
		const endDate = new Date(filters.period.to);
		const yearBefore = new Date(endDate.getFullYear() - 1, endDate.getMonth(), endDate.getDate());
		const parsedYearBefore = yearBefore.toISOString().split('T')[0];

		let paramStr = `filter[serial]=${filters.vehicle.serial}&filter[from.gte]=${parsedYearBefore}&filter[to.lte]=${filters.period.to}`;

		return this.httpClient
			.get<Response<any>>(environment.server + `/v2/maas/workload?${paramStr}`)
			.pipe(
				map((response) => {
					if (!response.status) {
						return [];
					}

					return response.data;
				})
			);
	}

	fetchMonthlyBusyTimeData(filters: MaasCostsFilters): Observable<HeatmapData[]> {
		let paramStr = `filter[serial]=${filters.vehicle.serial}&filter[from.gte]=${filters.period.from}&filter[to.lte]=${filters.period.to}`;

		return this.httpClient
			.get<Response<any>>(environment.server + `/v2/maas/workload?${paramStr}`)
			.pipe(
				map((response) => {
					if (!response.status) {
						return [];
					}

					return response.data;
				})
			);
	}

	fetchYearlyCostData(filters: MaasCostsFilters): Observable<CostData[]> {
		let iteratedPeriod = filters.period;
		const periods = [];
		periods.push(iteratedPeriod);
		for (let i = 0; i < 12; i++) {
			iteratedPeriod = this.getPreviousPeriod(iteratedPeriod);
			if (iteratedPeriod) {
				periods.push(iteratedPeriod);
			} else {
				break;
			}
		}
		const ids = periods.map((period) => period?.id).filter((id) => id != null);
		if (ids.length === 0) {
			return of([]);
		}

		const paramStr = `serial=${filters.vehicle.serial}&ids=${ids.join(',')}`;

		return this.httpClient
			.get<Response<any>>(environment.server + `/v2/maas/costs?${paramStr}`)
			.pipe(
				map((response) => {
					return response.data;
				})
			);
	}

	downloadPngChart(data: PngDownload) {
		/**
		 * Filtering out the unneeded elements from the chart such as the
		 * download button and the tooltip icon
		 */
		const filter = (node: HTMLElement) => {
			const excludedClasses = ['action-buttons'];
			return !excludedClasses.some((className) => node.classList?.contains(className));
		};

		htmlToImage
			.toPng(document.getElementById(data.id) as HTMLElement, {
				filter: filter,
				backgroundColor: 'var(--color-secondary-000)',
			})
			.then((dataUrl) => {
				const link = document.createElement('a');
				link.href = dataUrl;
				link.download = `${data.name}.png`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			});
	}

	fetchVehicles(search: string, page: Meta, serials?: string[]): Observable<Response<Vehicle>> {
		let params = HttpParamsBuilder.buildParameters(page, undefined, search, undefined);
		if (!search?.length && serials?.length) {
			params = params.append('prioritize[serial]', serials.join(','));
		}

		return this.httpClient.get<any>(environment.server + '/v2/maas/vehicles', { params });
	}

	fetchPeriods(vehicleSerial: string): Observable<Response<any>> {
		return this.httpClient.get<any>(
			environment.server + `/v2/maas/periods?serial=${vehicleSerial}`
		);
	}

	fetchDistinctPeriods(): Observable<MaasCostPeriod[]> {
		return this.httpClient
			.get<Response<any>>(environment.server + '/v2/maas/distinct-periods')
			.pipe(
				map((response) => {
					if (!response.status || !response.data) {
						return [];
					}
					return response.data as MaasCostPeriod[];
				})
			);
	}

	fetchPeriodsForSerials(serials: string[]): Observable<MaasCostPeriod[]> {
		return this.httpClient
			.get<
				Response<any>
			>(environment.server + `/v2/maas/periods-for-serials?serials=${serials.join(',')}`)
			.pipe(
				map((response) => {
					if (!response.status || !response.data) {
						return [];
					}
					return response.data as MaasCostPeriod[];
				})
			);
	}

	fetchAvailableYears(serials: string[]): Observable<MaasAvailableYears> {
		return this.httpClient
			.get<MaasAvailableYearsResponse>(
				environment.server + `/v2/maas/available-years?serials=${serials.join(',')}`
			)
			.pipe(
				map((response) => {
					if (response.message === MaasAvailableYearsMessage.EndpointNotUsableForVehicles) {
						return { years: [], unusableSerials: response.serials ?? [] };
					}
					return { years: response.status ? (response.data ?? []) : [], unusableSerials: [] };
				})
			);
	}

	fetchOverview(
		from: string,
		to: string,
		serials: string[]
	): Observable<MaasOverviewResponse | null> {
		const params = `?from=${from}&to=${to}&serials=${serials.join(',')}`;
		return this.httpClient
			.get<MaasOverviewResponse>(environment.server + `/v2/maas/overview${params}`)
			.pipe(
				map((response) => {
					if (!response.status) {
						return null;
					}
					return response;
				})
			);
	}

	fetchYearlyOverview(
		serials: string[],
		year: number
	): Observable<MaasYearlyOverviewResponse | null> {
		const params = `?serials=${serials.join(',')}&year=${year}`;
		return this.httpClient
			.get<MaasYearlyOverviewRawResponse>(environment.server + `/v2/maas/yearly-overview${params}`)
			.pipe(
				map((response) => {
					if (!response.status) {
						return null;
					}
					const data = Array.isArray(response.data)
						? response.data
						: Object.values(response.data ?? {});
					return { ...response, data };
				})
			);
	}

	exportYearlyOverview(serials: string[], year: number): Observable<MaasOverviewExportResponse> {
		const params = `?serials=${serials.join(',')}&year=${year}&export=true`;
		return this.httpClient.get<MaasOverviewExportResponse>(
			environment.server + `/v2/maas/yearly-overview${params}`
		);
	}

	getOverviewVehicles(): Vehicle[] {
		return this.userStoreService.currentAppSettings()?.maasOverview?.vehicles ?? [];
	}

	getOverviewPeriod(): MaasCostPeriod | null {
		return this.userStoreService.currentAppSettings()?.maasOverview?.period ?? null;
	}

	saveOverviewSelection(vehicles: Vehicle[], period: MaasCostPeriod | null): Observable<void> {
		const current = this.userStoreService.currentAppSettings();
		if (!current?.id) {
			return of(undefined);
		}
		return this.userStoreService.updateAppSettings({
			...current,
			maasOverview: { vehicles, period: vehicles.length ? period : null },
		});
	}

	saveOverviewVehicles(vehicles: Vehicle[]): Observable<void> {
		const current = this.userStoreService.currentAppSettings();
		if (!current?.id) {
			return of(undefined);
		}
		const period = vehicles.length ? (current.maasOverview?.period ?? null) : null;
		return this.userStoreService.updateAppSettings({
			...current,
			maasOverview: { vehicles, period },
		});
	}

	exportOverview(
		from: string,
		to: string,
		serials: string[]
	): Observable<MaasOverviewExportResponse> {
		const params = `?from=${from}&to=${to}&serials=${serials.join(',')}&export=true`;
		return this.httpClient.get<MaasOverviewExportResponse>(
			environment.server + `/v2/maas/overview${params}`
		);
	}

	updateFilters(vehicle: Vehicle, period: MaasCostPeriod) {
		this._filters.set({
			period: period,
			vehicle: vehicle,
		});
	}

	resetFilters() {
		this._filters.set(undefined);
		this.availablePeriods.set([]);
	}

	fetchCostBreakdown(filters: MaasCostsFilters): Observable<CostBreakdown> {
		if (filters.period.current_period) {
			return of({
				date: '',
				id: 0,
				from: '',
				to: '',
				amount: 0,
				fixed: 0,
				variable: 0,
				usage: 0,
			});
		}

		let paramStr = `serial=${filters.vehicle.serial}&ids=${filters.period.id}`;
		const costs = this.httpClient
			.get<Response<any>>(environment.server + `/v2/maas/costs?${paramStr}`)
			.pipe(
				map((response) => {
					if (!response.status || !response.data || response.data.length === 0) {
						return {
							date: '',
							id: 0,
							from: '',
							to: '',
							amount: 0,
							fixed: 0,
							variable: 0,
						};
					}
					return response.data[0];
				})
			);

		const usage = this.fetchUsageDataForTimePeriod(
			filters.vehicle,
			filters.period.from,
			filters.period.to
		).pipe(
			map((data) => {
				return data || 0;
			})
		);

		return forkJoin([costs, usage]).pipe(
			map(([costData, usage]) => {
				return {
					...costData,
					usage: usage,
				};
			})
		);
	}

	fetchForecastedUsage(filters: MaasCostsFilters): Observable<ForecastedUsage> {
		const { periodLengthInDays } = this.calculatePeriodLengthAndEffectiveToDate(filters.period);
		const elapsedDaysSinceStart = this.calculateElapsedDaysSinceStart(filters.period.from);

		const historicalPeriods = this.getHistoricalPeriods(filters.period, 6);

		const currentPeriodUsage$ = this.fetchCurrentPeriodUsage(filters.vehicle, filters.period);
		const historicalUsages$ = this.fetchHistoricalUsages(filters.vehicle, historicalPeriods);

		return forkJoin({
			currentUsage: currentPeriodUsage$,
			historicalUsages: historicalUsages$,
		}).pipe(
			map(({ currentUsage, historicalUsages }) =>
				this.calculateForecastUsage(
					currentUsage,
					historicalUsages,
					elapsedDaysSinceStart,
					periodLengthInDays
				)
			)
		);
	}

	private fetchHistoricalUsages(
		vehicle: Vehicle,
		historicalPeriods: MaasCostPeriod[]
	): Observable<number[]> {
		if (!historicalPeriods.length) {
			return of([]);
		}

		return forkJoin(
			historicalPeriods.map((p) =>
				this.fetchUsageDataForTimePeriod(vehicle, p.from, p.to).pipe(map((val) => val ?? 0))
			)
		);
	}

	private getHistoricalPeriods(period: MaasCostPeriod, count: number): MaasCostPeriod[] {
		const historicalPeriods: MaasCostPeriod[] = [];
		let iteratedPeriod = period;

		for (let i = 0; i < count; i++) {
			iteratedPeriod = this.getPreviousPeriod(iteratedPeriod);
			if (!iteratedPeriod) break;
			historicalPeriods.unshift(iteratedPeriod);
		}

		return historicalPeriods;
	}

	private calculatePeriodLengthAndEffectiveToDate(period: MaasCostPeriod): {
		periodLengthInDays: number;
		effectiveToDate: string;
	} {
		if (period.current_period) {
			return this.calculateCurrentPeriodLengthAndToDate(period);
		} else {
			return this.calculateCompletedPeriodLengthAndToDate(period);
		}
	}

	private calculateCurrentPeriodLengthAndToDate(period: MaasCostPeriod): {
		periodLengthInDays: number;
		effectiveToDate: string;
	} {
		const previousPeriod = this.getPreviousPeriod(period);

		if (previousPeriod) {
			const periodLengthInDays = this.calculatePeriodLengthInDays(
				previousPeriod.from,
				previousPeriod.to
			);
			const effectiveToDate = this.calculateEffectiveToDate(period.from, periodLengthInDays);
			return { periodLengthInDays, effectiveToDate };
		} else {
			// Fallback: use 30 days if no previous period exists
			const periodLengthInDays = 30;
			const effectiveToDate = this.calculateEffectiveToDate(period.from, periodLengthInDays);
			return { periodLengthInDays, effectiveToDate };
		}
	}

	private calculateCompletedPeriodLengthAndToDate(period: MaasCostPeriod): {
		periodLengthInDays: number;
		effectiveToDate: string;
	} {
		const periodLengthInDays = this.calculatePeriodLengthInDays(period.from, period.to);
		return { periodLengthInDays, effectiveToDate: period.to };
	}

	private calculatePeriodLengthInDays(from: string, to: string): number {
		return Math.ceil((new Date(to).getTime() - new Date(from).getTime()) / (1000 * 60 * 60 * 24));
	}

	private calculateEffectiveToDate(from: string, periodLengthInDays: number): string {
		const fromDate = new Date(from);
		const effectiveToDateObj = new Date(
			fromDate.getTime() + periodLengthInDays * 24 * 60 * 60 * 1000
		);
		return effectiveToDateObj.toISOString().split('T')[0]; // Format as YYYY-MM-DD
	}

	private calculateElapsedDaysSinceStart(from: string): number {
		return Math.ceil((new Date().getTime() - new Date(from).getTime()) / (1000 * 60 * 60 * 24));
	}

	private fetchCurrentPeriodUsage(vehicle: Vehicle, period: MaasCostPeriod): Observable<number> {
		return this.fetchUsageDataForTimePeriod(vehicle, period.from, period.to).pipe(
			map((data) => data || 0)
		);
	}

	private calculateForecastUsage(
		currentUsage: number,
		historicalUsages: number[],
		elapsedDaysSinceStart: number,
		periodLengthInDays: number
	): ForecastedUsage {
		if (elapsedDaysSinceStart === 0) {
			return {
				usage: 0,
				percentageDifference: 0,
			};
		}

		const currentEstimatedFullPeriod = (currentUsage / elapsedDaysSinceStart) * periodLengthInDays;
		const data = [...historicalUsages, currentEstimatedFullPeriod];
		const forecastResult = forecast(data, 1, 'auto');

		const usage = forecastResult.predicted[0];
		const lastKnownPeriodUsage =
			historicalUsages.length > 0 ? historicalUsages[historicalUsages.length - 1] : 0;
		const percentageDifference = lastKnownPeriodUsage
			? ((usage - lastKnownPeriodUsage) / lastKnownPeriodUsage) * 100
			: 0;

		return {
			usage: usage,
			percentageDifference: percentageDifference,
		};
	}
}
